import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterModule, RouterOutlet } from '@angular/router';
import { NgbCollapseModule, NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FooterComponent } from '@/app/components/footer/footer.component';
import { CommonModule, AsyncPipe } from '@angular/common';
import { VerticalMenuButtonComponent } from '@/app/components/menu/components/menu-button';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, map, filter } from 'rxjs';
import { getAuthMe, getToken, getisLoggedIn, getUser } from '@/app/store/auth/auth.selector';
import { loadMe } from '@/app/store/auth/auth.actions';
import { AuthenticationService } from '@/app/core/services/config/auth.service';
import { User, AuthMe } from '@core/models/auth';
import type { MenuItemType } from '@core/models/menu';

@Component({
  selector: 'layout-user',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    RouterModule,
    RouterOutlet,
    RouterLink,
    NgbCollapseModule,
    NgbDropdownModule,
    NgbTooltipModule,
    VerticalMenuButtonComponent,
    FooterComponent,
  ],
  templateUrl: './user-layout.component.html',
  styleUrl: './user-layout.component.scss',
})
export class UserLayoutComponent implements OnInit, OnDestroy {

  usermenuItems: MenuItemType[] = [
    { key: 'acc-user-dashboard', label: 'Dashboard', url: '/user/dashboard', parentKey: 'acc-user', icon: 'bi bi-grid' },
    { key: 'acc-user-search', label: 'Buscar vuelos', url: '/user/search-engine', parentKey: 'acc-user', icon: 'bi bi-search' },
    { key: 'acc-user-purchases', label: 'Mis viajes', url: '/user/my-purchases', parentKey: 'acc-user', icon: 'bi bi-airplane' },
    { key: 'acc-user-payments', label: 'Pagos y reembolsos', url: '/user/payments', parentKey: 'acc-user', icon: 'bi bi-credit-card' },
    { key: 'acc-user-promotions', label: 'Promociones', url: '/user/promotions', parentKey: 'acc-user', icon: 'bi bi-tag' },
    { key: 'acc-user-referrals', label: 'Referidos', url: '/user/referrals', parentKey: 'acc-user', icon: 'bi bi-people' },
    { key: 'acc-user-profile', label: 'Mi Perfil', url: '/user/profile', parentKey: 'acc-user', icon: 'bi bi-person' },
  ]

  currentPath: string | null = null
  drawerOpen = false
  userDrawerOpen = false
  menuDropdownOpen = false
  empresasOpen = false
  isLoggedIn$!: Observable<boolean>
  token$!: Observable<string | undefined>
  authShellVisible$!: Observable<boolean>
  hasCookieSession = false

  private user$: Observable<User | null>
  private me$: Observable<AuthMe | null>

  profile$!: Observable<{
    fullName: string
    initials: string
    email: string
    photo: string
  }>

  constructor(
    private store: Store,
    private authService: AuthenticationService,
    private router: Router,
  ) {
    this.isLoggedIn$ = this.store.select(getisLoggedIn)
    this.token$ = this.store.select(getToken)
    this.user$ = this.store.select(getUser)
    this.me$ = this.store.select(getAuthMe)

    this.authShellVisible$ = combineLatest([this.isLoggedIn$, this.token$]).pipe(
      map(([isLoggedIn, token]) => isLoggedIn && (!!token || !!this.authService.session))
    )

    this.hasCookieSession = !!this.authService.session

    this.profile$ = combineLatest([this.user$, this.me$]).pipe(
      filter(([user, me]) => !!user || !!me),
      map(([user, me]) => {
        const fullName = me
          ? `${me.name || ''} ${me.last_name || ''}`.trim()
          : user?.name || user?.firstName || ''
        const initials = fullName
          .split(' ')
          .slice(0, 2)
          .map((n: string) => n[0]?.toUpperCase() || '')
          .join('')
        return {
          fullName,
          initials,
          email: me?.email || user?.email || '',
          photo: me?.imgUrl || user?.avatar || 'assets/images/avatar/01.jpg',
        }
      })
    )

    if (this.authService.session) {
      this.store.dispatch(loadMe())
    }
  }

  ngOnInit(): void {
    this.currentPath = this.router.url
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => { this.currentPath = e.urlAfterRedirects })
  }

  isActive(url?: string): boolean {
    if (!url) return false
    const path = (this.currentPath || '').split('?')[0].split('#')[0]
    return path === url || path.startsWith(url + '/') || path.startsWith(url + '(')
  }

  firstName(fullName?: string): string {
    return (fullName || 'Usuario').trim().split(' ')[0]
  }

  ngAfterViewInit(): void {
    document.body.classList.add('dashboard')
  }

  ngOnDestroy(): void {
    document.body.classList.remove('dashboard')
  }
}